import type { z } from 'zod';
import type { ApiErrorCode, ApiFailureCode } from './common';
import {
  buildPath,
  buildQuery,
  type Endpoint,
  type EndpointBody,
  type EndpointParams,
  type EndpointQuery,
  type EndpointResponse,
  type FieldIfPresent,
  IDEMPOTENCY_HEADER,
} from './http';
import { asErrorMessageKey, type ErrorMessageKey } from './messages';

/**
 * The typed fetch client every frontend uses. Zero dependencies beyond zod, and
 * no Hono import - Hono stays confined to apps/api-worker (tech-stack section 2).
 *
 * The base URL is injected, never hardcoded: every absolute URL in the platform
 * derives from configuration so the move to croissantinbed.com is a config change
 * and not a code change (PRD OPS-018).
 */

export interface ApiClientOptions {
  /** e.g. https://api.croissantinbed.com - from VITE_API_BASE_URL, never a literal. */
  baseUrl: string;
  /** Extra headers per request: bearer tokens, guest session token, locale. */
  headers?: () => Promise<Record<string, string>> | Record<string, string>;
  fetchImpl?: typeof fetch;
  /** Parses responses with the endpoint's zod schema. On by default. */
  validateResponses?: boolean;
  /**
   * Cookie policy for every call. Left unset - the fetch default,
   * `same-origin` - the platform's own auth is entirely header-based
   * (Authorization, x-guest-session, x-bakery-device) and needs no cookies.
   *
   * `'include'` exists for the founder's pilot gate (docs/pilot-access.html), whose
   * session IS a cookie: the staff portals are on `baker.`/`internal.`/`www.`
   * and the API is on `api.`, so without this a cross-origin call sends no
   * cookie and every request arrives anonymous. Safe to set on those portals
   * because the worker's CORS layer echoes only its own allowlisted origins and
   * never `*` - a wildcard origin with credentials is refused by browsers
   * outright, which is the failure mode this would otherwise invite.
   *
   * Spelled out rather than typed as the DOM's `RequestCredentials`: this module
   * is compiled by `apps/api-worker` too, where the only `lib` is
   * `@cloudflare/workers-types` and that name does not exist.
   */
  credentials?: ApiCredentials;
}

/**
 * The fetch credentials modes, named here because the two toolchains that
 * compile this file disagree about them: a browser's `RequestInit` has
 * `credentials`, and a Worker's does not - a Worker's own fetch has no cookie
 * jar to apply a policy to. The option is therefore attached through a widened
 * init below rather than typed against whichever `RequestInit` happens to be in
 * scope, because dropping it under the Worker's lib would silently
 * un-authenticate every pilot request in the browsers that DO have one.
 */
export type ApiCredentials = 'omit' | 'same-origin' | 'include';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiFailureCode;
  /**
   * i18n catalogue key, so the caller renders the failure in the reader's
   * locale - always one of the registered keys (`messages.ts`), never whatever
   * arrived on the wire. An unregistered key is narrowed to `error.unexpected`
   * here rather than passed on, so no screen can ever print a raw key.
   */
  readonly messageKey: ErrorMessageKey;
  readonly fields: Record<string, string> | undefined;
  readonly requestId: string | undefined;
  /**
   * When the thing that is locked stops being locked - `ApiError.error.until`,
   * carried by `pin_locked` and nothing else today.
   *
   * Kept off `fields` here because it is kept off `fields` on the wire: the
   * counter RENDERS it ("Reessayez dans N minutes") rather than pointing at an
   * input. It was missing from this class until the counter needed it, and the
   * effect was silent and total - the envelope's instant was parsed, dropped
   * here, and every lockout on the tablet read "one minute" however long it
   * actually had to wait.
   */
  readonly until: string | undefined;

  constructor(init: {
    status: number;
    code: ApiFailureCode;
    message: string;
    messageKey: ErrorMessageKey;
    fields?: Record<string, string>;
    requestId?: string;
    until?: string;
  }) {
    super(init.message);
    this.name = 'ApiRequestError';
    this.status = init.status;
    this.code = init.code;
    this.messageKey = init.messageKey;
    this.fields = init.fields;
    this.requestId = init.requestId;
    this.until = init.until;
  }
}

export type CallArgs<E> = FieldIfPresent<'params', EndpointParams<E>> & {
  query?: EndpointQuery<E>;
} & FieldIfPresent<'body', EndpointBody<E>> & {
    /** Required when the endpoint declares `idempotent: true`. */
    idempotencyKey?: string;
    signal?: AbortSignal;
  };

// biome-ignore lint/suspicious/noExplicitAny: the descriptor's own generics are recovered by CallArgs/EndpointResponse.
type AnyEndpoint = Endpoint<z.ZodType, any, any, any>;

export class ApiClient {
  readonly #baseUrl: string;
  readonly #headers: ApiClientOptions['headers'];
  readonly #fetch: typeof fetch;
  readonly #validate: boolean;
  readonly #credentials: ApiCredentials | undefined;

  constructor(options: ApiClientOptions) {
    this.#baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.#headers = options.headers;
    this.#fetch = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.#validate = options.validateResponses ?? true;
    this.#credentials = options.credentials;
  }

  async call<E extends AnyEndpoint>(
    endpoint: E,
    args: CallArgs<E> = {} as CallArgs<E>,
  ): Promise<EndpointResponse<E>> {
    const raw = args as {
      params?: Record<string, string | number>;
      query?: Record<string, unknown>;
      body?: unknown;
      idempotencyKey?: string;
      signal?: AbortSignal;
    };

    const url = `${this.#baseUrl}${buildPath(endpoint.path, raw.params ?? {})}${buildQuery(raw.query)}`;

    const headers: Record<string, string> = {
      accept: 'application/json',
      ...((await this.#headers?.()) ?? {}),
    };
    if (raw.body !== undefined) headers['content-type'] = 'application/json';
    if (endpoint.idempotent) {
      if (!raw.idempotencyKey) {
        throw new Error(`Endpoint ${endpoint.method} ${endpoint.path} requires an idempotency key`);
      }
      headers[IDEMPOTENCY_HEADER] = raw.idempotencyKey;
    }

    let response: Response;
    try {
      // Widened deliberately: see `ApiCredentials`. A Worker's `RequestInit`
      // declares no `credentials`, so naming the field in a literal typed
      // against it is a compile error even though every browser honours it.
      const init: Record<string, unknown> = {
        method: endpoint.method,
        headers,
        ...(this.#credentials === undefined ? {} : { credentials: this.#credentials }),
        ...(raw.body !== undefined ? { body: JSON.stringify(raw.body) } : {}),
        ...(raw.signal ? { signal: raw.signal } : {}),
      };
      response = await this.#fetch(url, init as RequestInit);
    } catch (cause) {
      throw new ApiRequestError({
        status: 0,
        code: 'network_error',
        message: cause instanceof Error ? cause.message : 'Network request failed',
        messageKey: 'error.network',
      });
    }

    const requestId = response.headers.get('x-request-id') ?? undefined;
    const text = await response.text();
    const payload: unknown = text ? safeJsonParse(text) : undefined;

    if (!response.ok) {
      const shape = payload as
        | {
            error?: {
              code?: ApiErrorCode;
              message?: string;
              messageKey?: string;
              fields?: Record<string, string>;
              until?: string;
            };
          }
        | undefined;
      throw new ApiRequestError({
        status: response.status,
        code: shape?.error?.code ?? 'internal',
        message: shape?.error?.message ?? `Request failed with status ${response.status}`,
        // Narrowed against the registry, not trusted: a worker ahead of this
        // build can name a key this app has no copy for, and the honest answer
        // is the generic sentence rather than the raw key on screen.
        messageKey: asErrorMessageKey(shape?.error?.messageKey) ?? 'error.unexpected',
        ...(shape?.error?.fields ? { fields: shape.error.fields } : {}),
        ...(requestId ? { requestId } : {}),
        // The envelope's own field, not one of `fields`. A build that does not
        // know the code ignores it, which is what the contract's optional means.
        ...(typeof shape?.error?.until === 'string' ? { until: shape.error.until } : {}),
      });
    }

    if (!this.#validate) return payload as EndpointResponse<E>;

    const parsed = endpoint.response.safeParse(payload);
    if (!parsed.success) {
      throw new ApiRequestError({
        status: response.status,
        code: 'invalid_response',
        message: `Response did not match the contract for ${endpoint.method} ${endpoint.path}`,
        messageKey: 'error.unexpected',
        ...(requestId ? { requestId } : {}),
      });
    }
    return parsed.data as EndpointResponse<E>;
  }
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  return new ApiClient(options);
}
